import { Card } from '../../../../components/Card/Card';
import { useCallback, useContext, useEffect, useState } from 'react';
import { PatientRecord } from '../../../../shared/types/types';
import './patientCodeVerifyCard.scss';
import { Button } from 'src/components/Button/Button';
import { ExclamationOutlined } from 'src/icons/ExclamationOutlined';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';
import { v4 } from 'uuid';
import { TAS_BACK_END_API_URL, URL_TAS_VERSION } from 'src/constants';
import { fetchAuthenticated } from 'src/controllers';
import { LoadingSpinner, mediumSpinner, smallSpinner } from 'src/components/common/loader/LoadingSpinner';

export interface UpdatePatientInfoProps {
  patientData?: Partial<PatientRecord>;
}

export interface PatientCodeVerifyCardProps {
  setPatientVerifyCard: (value: boolean) => void;
  medicalId?: string;
  patientId?: string;
  token: string | null;
}

export interface InfusionDetails {
  _id: string;
  user: {
    id: string;
    name: string;
  };
  createdDateTime: string;
  lastModifiedDateTime: string;
  patientId: string;
  infusionDrug: string;
  infusionProtocol: string;
  pkModel?: string;
  infusion_dose_ppm: string;
  lastModifiedBy: string; // user ID
  _version: number;
  history: any[];
}

export interface InfusionDetailsServiceResult {
  status: string;
  item: InfusionDetails;
  errors: string[];
}

export interface InfusionDetailsAjaxResult {
  result: InfusionDetailsServiceResult;
}

export function PatientCodeVerifyCard({ setPatientVerifyCard, medicalId, patientId, token }: PatientCodeVerifyCardProps) {
  const [expanded, setIsExpanded] = useState<boolean>(true);
  const [drugModelExpanded, setDrugModelExpanded] = useState<boolean>(true);
  const [infusionDrug, setInfusionDrug] = useState('');
  const [infusionProtocol, setInfusionProtocol] = useState('');
  const [pkModel, setPkModel] = useState('PK_MODEL_NONE');
  const [infusion_dose_ppm, setInfusionDose] = useState<string>('');
  const [previousData, setPreviousData] = useState<any>(null); // Will be used to check if the previous vs current info has changed
  const [isLoadingDrugModel, setIsLoadingDrugModel] = useState<boolean | 'failed'>(false);
  const [verificationLoading, setVerificationLoading] = useState<boolean>(false);
  const { addBannerMessage } = useContext(BannerMessageContext);

  const [checkInId, setCheckInId] = useState('');

  const getInfusionDetails = useCallback(async () => {
    if (!patientId) return;
    const url = TAS_BACK_END_API_URL + `${URL_TAS_VERSION}/patients/${patientId}/infusiondetails`;
    const config: RequestInit = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    let response;
    try {
      response = await fetchAuthenticated<InfusionDetailsAjaxResult>(url, config);
      if (response?.ok) {
        setIsLoadingDrugModel(true);
        if (response?.parsedBody?.result?.item !== null) {
          const data = response?.parsedBody?.result?.item;
          setPkModel(data?.pkModel || 'PK_MODEL_NONE');
          setInfusionDrug(data?.infusionDrug || '');
          setInfusionProtocol(data?.infusionProtocol || '');
          setInfusionDose(data?.infusion_dose_ppm || '');
          setPreviousData(data);
        }
      }
    } catch (error) {
      setIsLoadingDrugModel('failed');
      addBannerMessage({
        id: v4(),
        type: 'error',
        message: 'Uh oh! There was an error getting the Infusion Protocol and Model Data. Please refresh to try again.',
      });
    }
  }, [token, addBannerMessage, patientId]);

  useEffect(() => {
    token && patientId && getInfusionDetails();
  }, [getInfusionDetails, token, patientId]);

  const handlePatientInfoExpand = () => {
    setIsExpanded(!expanded);
  };

  const handleDrugModelExpand = () => {
    setDrugModelExpanded(!drugModelExpanded);
  };

  const handlePatientVerification = async () => {
    if (medicalId !== checkInId) {
      addBannerMessage({
        id: v4(),
        message: 'Patient Code does not match the input! Double check the patient and medicalId before trying again',
        type: 'error',
      });
      return;
    }

    let validInfo = false;
    if (infusion_dose_ppm === '' || infusionDrug === '' || infusionProtocol === '') {
      addBannerMessage({
        id: v4(),
        message: 'Please Complete Selections for Protocol Drug and Model Type before Verification',
        type: 'error',
      });
      return;
    } else {
      setVerificationLoading(true);
      try {
        // send data to backend collection
        if (
          previousData &&
          previousData.pkModel === pkModel &&
          previousData.infusionDrug === infusionDrug &&
          previousData.infusionProtocol === infusionProtocol &&
          previousData.infusion_dose_ppm === infusion_dose_ppm
        ) {
          validInfo = true;
        } else if (!previousData || !previousData._id || !previousData._version) {
          const url = `${TAS_BACK_END_API_URL}${URL_TAS_VERSION}/patients/${patientId}/infusiondetails`;

          const body = {
            infusionDrug: infusionDrug,
            pkModel: pkModel,
            infusionProtocol: infusionProtocol,
            infusion_dose_ppm: infusion_dose_ppm,
          };

          const config: RequestInit = {
            method: 'PUT',
            body: JSON.stringify(body),
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };

          const response = await fetchAuthenticated<any>(url, config);
          if (response?.ok) {
            validInfo = true;
          }
        } else {
          const url = `${TAS_BACK_END_API_URL}${URL_TAS_VERSION}/patients/${patientId}/infusiondetails/${previousData._id}`;

          const body = {
            _version: previousData._version,
            infusionDrug: infusionDrug,
            pkModel: pkModel,
            infusionProtocol: infusionProtocol,
            infusion_dose_ppm: infusion_dose_ppm,
          };

          const config: RequestInit = {
            method: 'PATCH',
            body: JSON.stringify(body),
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };

          const response = await fetchAuthenticated<any>(url, config);
          if (response?.ok) {
            validInfo = true;
          }
        }
      } catch (error) {
        setVerificationLoading(false);
        addBannerMessage({
          id: v4(),
          message: 'There was an error updating the Drug Protocol and Model Type before Verification. Please try again!',
          type: 'error',
        });
      }
    }

    setVerificationLoading(false);
    if (validInfo) {
      setPatientVerifyCard(true);
      addBannerMessage({
        id: v4(),
        message: 'Patient Verified!',
        type: 'success',
      });
    }
  };
  const handlePatientInfoChange = (value: string) => {
    setCheckInId(value);
  };

  const handleInputChange = (event: any, inputType: string) => {
    if (inputType === 'drug') {
      if (event.target.value === 'INFUSION_DRUG_BPA') {
        setInfusionProtocol('INFUSION_DRUG_BPA');
      } else if (event.target.value === 'INFUSION_DRUG_BTS') {
        setInfusionProtocol('INFUSION_DRUG_BTS');
      }
      setInfusionDrug(event.target.value);
    } else if (inputType === 'protocol') {
      if (event.target.value === 'Brookhaven') {
        setInfusionProtocol('INFUSION_PROTOCOL_BROOKHAVEN');
      } else if (event.target.value === 'Continuous') {
        setInfusionProtocol('INFUSION_PROTOCOL_CONTINUOUS');
        setPkModel('PK_MODEL_NONE');
      }
    } else if (inputType === 'pk') {
      if (event.target.value === '') {
        setPkModel('PK_MODEL_NONE');
      } else {
        setPkModel(event.target.value);
      }
    } else if (inputType === 'dose') {
      setInfusionDose(event.target.value);
    }
  };

  const body = !isLoadingDrugModel ? (
    <div className="loading-container">
      <LoadingSpinner width={mediumSpinner} height={mediumSpinner} />
    </div>
  ) : (
    <div className="patient-checkin-container">
      <div className="input-container">
        <div className="label-info-container">
          <div className="label">Please Type or Scan Patient Code</div>
          <input
            className="textfield"
            placeholder={'                 XXXXX-XXXXX-XXXXX'}
            onChange={(e) => {
              handlePatientInfoChange(e.target.value);
            }}
            value={checkInId}
          ></input>
        </div>
      </div>

      <div className={'checkin-container'}>
        <Button
          label={verificationLoading ? '' : 'Verify'}
          onClick={() => handlePatientVerification()}
          className="edit-button"
          disabled={verificationLoading}
          icon={verificationLoading ? <LoadingSpinner width={smallSpinner} height={smallSpinner} /> : <ExclamationOutlined />}
          iconPlacement="right"
        />
      </div>
    </div>
  );

  const drugModelBody =
    isLoadingDrugModel === true ? (
      <div className="drugModel">
        <div className="checkin-container">
          <div className="dropdown-container">
            <div className="dropDown">
              <div className="labelName">Infusion Drug:</div>
              <select
                className="dropDownField"
                value={infusionDrug === 'INFUSION_DRUG_BPA' ? 'INFUSION_DRUG_BPA' : infusionDrug === 'INFUSION_DRUG_BTS' ? 'INFUSION_DRUG_BTS' : ''}
                onChange={(event) => handleInputChange(event, 'drug')}
                disabled={verificationLoading}
              >
                <option value="" disabled>
                  Selection Required
                </option>
                <option value="INFUSION_DRUG_BPA">BPA</option>
                {/* <option value="INFUSION_DRUG_BTS">BTS</option> */}
              </select>
            </div>
            <div className="dropDown">
              <div className="labelName">Infusion Protocol:</div>
              <select
                className="dropDownField"
                value={infusionProtocol === 'INFUSION_PROTOCOL_BROOKHAVEN' ? 'Brookhaven' : infusionProtocol === 'INFUSION_PROTOCOL_CONTINUOUS' ? 'Continuous' : ''}
                onChange={(event) => handleInputChange(event, 'protocol')}
                disabled={verificationLoading}
              >
                <option value="" disabled>
                  Selection Required
                </option>

                <option value="Continuous">Continuous</option>
                <option value="Brookhaven">Brookhaven</option>
              </select>
            </div>

            <div className="dropDown">
              <div className="labelName">Infusion Dose (mg/kg):</div>
              <select className="dropDownFieldWide" value={infusion_dose_ppm} onChange={(event) => handleInputChange(event, 'dose')} disabled={verificationLoading}>
                <option value="" disabled>
                  Selection Required
                </option>
                {infusionProtocol === 'INFUSION_PROTOCOL_BROOKHAVEN' && <option value="Phase 1: 1300">Phase 1: 1300</option>}
                {infusionProtocol === 'INFUSION_PROTOCOL_BROOKHAVEN' && <option value="Phase 1: 1400">Phase 1: 1400</option>}
                {infusionProtocol === 'INFUSION_PROTOCOL_BROOKHAVEN' && <option value="Phase 1: 1500">Phase 1: 1500</option>}
                {infusionProtocol === 'INFUSION_PROTOCOL_BROOKHAVEN' && <option value="Phase 1: 1600">Phase 1: 1600</option>}
                {infusionProtocol !== 'INFUSION_PROTOCOL_BROOKHAVEN' && <option value="Phase 1: 1500 Phase 2: 750">Phase 1: 1500 Phase 2: 750</option>}
                {infusionProtocol !== 'INFUSION_PROTOCOL_BROOKHAVEN' && <option value="Phase 1: 1400 Phase 2: 700">Phase 1: 1400 Phase 2: 700</option>}
                {infusionProtocol !== 'INFUSION_PROTOCOL_BROOKHAVEN' && <option value="Phase 1: 1300 Phase 2: 650">Phase 1: 1300 Phase 2: 650</option>}
              </select>
            </div>

            {infusionProtocol === 'INFUSION_PROTOCOL_BROOKHAVEN' ? (
              <div className="dropDown">
                <div className="labelName">PK Model:</div>
                <select className="dropDownField" value={pkModel} onChange={(event) => handleInputChange(event, 'pk')} disabled={verificationLoading}>
                  <option value="PK_MODEL_NONE">Selection Required</option>
                  <option value="PK_MODEL_M">Model M</option>
                </select>
              </div>
            ) : (
              <div className="dropDown">
                <div className="takeSpace"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    ) : !isLoadingDrugModel ? (
      <div className="loading-container">
        <LoadingSpinner width={mediumSpinner} height={mediumSpinner} />
      </div>
    ) : (
      <></>
    );

  return (
    <div className="patient-info-container">
      <Card
        title={'Drug Protocol and Model Type'}
        body={drugModelExpanded ? drugModelBody : <></>}
        onExpand={handleDrugModelExpand}
        expandable={true}
        expanded={drugModelExpanded}
      />
      <Card title={'PATIENT Verification'} body={expanded ? body : <></>} onExpand={handlePatientInfoExpand} expandable={true} expanded={expanded} />
    </div>
  );
}
